/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */


import {DefaultAddresses} from "./Addresses.js";
import {EmitterReplyListener} from "./EmitterReplyListener.js";
import {Once, TimeoutForOnce, ErrorInstruction} from "./consts.js";

/***
 * Bidirectional RPC event for the Kokabo protocol.
 *
 * "Bidirectional" means receivers can reply to inbound calls using the same callId + inverted addresses,
 * which keeps routing explicit and avoids hidden global state.
 *
 * @extends CustomEvent
 */
export class ActorEvent extends CustomEvent {

    /**
     * @param type {string}
     * @param {Object} options
     * @param actor {Actor}
     * @param callId {string}
     * @param instruction {string}
     * @param {Addresses} addresses
     * @throws Error if required protocol fields are missing (prevents emitting malformed RPC frames).
     */
    constructor(type, options, actor, callId, instruction, addresses) {
        super(type, options);
        if (type === undefined || type === null || type === "") {
            throw new Error("Event type is required");
        }
        if (actor === undefined) {
            throw new Error("Hub instance is required");
        }
        if (callId === undefined || callId === null || callId === "") {
            throw new Error("Call id is required");
        }
        this.sender = actor;
        this.callId = callId;
        this.instruction = instruction;
        this.addresses = addresses;
    }

    isCall() {
        return this.instruction === 'c';
    }

    isAnswer() {
        return this.instruction === 'a';
    }

    isError() {
        return this.instruction === 'e';
    }


    /**
     * Callback-based call primitive used by both Actor.call(...) and Actor.callPromise(...).
     *
     * Delivery contract:
     * - success(event) is invoked on a remote answer event (instruction `'a'`)
     * - failure(event) is invoked on:
     *     - a remote error reply (instruction `'e'`)
     *     - a timeout (synthetic error event, instruction `'e'`)
     *     - a local send failure (synthetic error event, instruction `'e'`)
     *
     * Local send contract:
     * - The returned Promise represents the local send lifecycle (encryption + transport).
     * - It resolves when the call is locally sent and rejects on local send failure.
     *
     * NOTE:
     * - Timeouts are handled at the emitter level: when the timeout expires, the emitter is removed
     *   and `failure()` is invoked with a synthetic error event.
     *
     * Synthetic error event detail contract (stable fields):
     * - Timeout synthetic detail includes:
     *     { kind:"timeout", stage:"timeout", local:true, code:"E_TIMEOUT", retryable:true, message:"..." }
     * - Send failure synthetic detail includes:
     *     { kind:"transport", stage:"send", local:true, code:"E_TRANSPORT_SEND", retryable:true, message:"..." }
     *
     * Additionally:
     * - The synthetic event is still an ActorEvent with instruction `'e'`.
     * - On send failures, the synthetic event also has `event.localError` set to the underlying Error when available.
     *
     * @param {function(ActorEvent):void} success
     * @param {function(ActorEvent):void} failure
     * @param {Addresses} addresses
     * @param {number} [answerLimit=Once]
     * @param {number} [timeout=TimeoutForOnce]
     * @returns {Promise<void>}
     */
    call(success, failure, addresses = DefaultAddresses, answerLimit = Once, timeout = TimeoutForOnce) {
        if (answerLimit <= 0) {
            throw new Error("The answer limit should be greater than 0, use a trigger if you don't want any reply (hub.triggerEvent)");
        }

        const actor = this.sender;
        const callId = this.callId;
        const callName = this.type;

        /**
         * Synthetic ActorEvent used to keep failure callbacks uniform (always receives an ActorEvent)
         * even when the failure is purely local (timeout/send) and no remote reply exists.
         *
         * @param {string} message
         * @param {"send"|"timeout"} stage
         * @param {Error|undefined} localError
         * @returns {ActorEvent}
         */
        const localErrorEvent = (message, stage, localError = undefined) => {
            let kind = "remote";
            let code = "E_REMOTE";
            let retryable = false;

            if (stage === "timeout") {
                kind = "timeout";
                code = "E_TIMEOUT";
                retryable = true;
            } else if (stage === "send") {
                kind = "transport";
                code = "E_TRANSPORT_SEND";
                retryable = true;
            }

            const detail = {
                message: message,
                stage: stage,
                kind: kind,
                code: code,
                retryable: retryable,
                local: true
            };

            const ev = new ActorEvent(callName, {detail: detail}, actor, callId, ErrorInstruction, addresses);

            // Preserve the original error for stack traces without changing the public callback contract.
            ev.localError = localError;

            return ev;
        };

        const emitter = new EmitterReplyListener(
            actor,
            callId,
            callName,
            (event) => {
                if (event.isError()) {
                    failure(event);
                } else {
                    success(event);
                }
            },
            timeout,
            answerLimit,
            () => {
                // Timeout failures originate from a timer context; never let user exceptions escape that execution path.
                try {
                    failure(localErrorEvent(`Timeout waiting for reply`, "timeout"));
                } catch (_) {
                    // best-effort
                }
            }
        );

        actor.addEmitter(emitter);

        // Local send: on failure, cleanup emitter and keep failure callback shape stable via a synthetic error event.
        return actor.sendCallMessage(callId, callName, this.detail, addresses).catch((err) => {
            try {
                actor.removeEmitter(emitter);
            } catch (_) {
                // best-effort
            }
            try {
                const m = (err && err.message) ? err.message : String(err);
                failure(localErrorEvent(`Send failure: ${m}`, "send", (err instanceof Error) ? err : undefined));
            } catch (_) {
                // best-effort
            }
            throw err;
        });
    }

    /***
     * Reply routing is explicit: reply addresses are the inverse of the inbound addresses.
     * This keeps server-side routing stateless and predictable.
     * @param data {Object}
     * @param isErroneous {boolean}
     * @param preventDefault
     * @param stopImmediatePropagation
     * @returns {Promise<void>}
     */
    reply(data, isErroneous, preventDefault = true, stopImmediatePropagation = true) {
        if (preventDefault) {
            this.preventDefault();
        }
        if (stopImmediatePropagation) {
            this.stopImmediatePropagation();
        }
        const replyAddresses = this.addresses.inverse();
        if (isErroneous) {
            return this.sender.sendErrorMessage(this.callId, this.type, data, replyAddresses);
        } else {
            return this.sender.sendAnswerMessage(this.callId, this.type, data, replyAddresses);
        }
    }
}